import React from 'react';
import dayjs from 'dayjs';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MdClose,
  MdSend,
  MdLocationOn,
  MdDragIndicator,
  MdAdd,
  MdDelete,
  MdRoute,
  MdKeyboardArrowDown,
  MdDirectionsCar,
  MdPerson,
} from 'react-icons/md';
import { BiPackage, BiFile, BiTime } from 'react-icons/bi';
import DriverSelect from '@features/drivers/components/DriverSelect';
import PartsVehicleSelect from './PartsVehicleSelect';

export type FulfillmentMode = 'own' | 'third_party';

interface DeliveryRunPanelProps {
  run: any | null;
  drivers: any[];
  vehicles: any[];
  workflowsets: any[];
  templates: any[];
  submitting: boolean;
  onUpdateRun: (field: string, value: any) => void;
  onRemoveOrder: (orderId: number) => void;
  onRemoveDestination: (key: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onQuickAdd: (dest: any) => void;
  onLoadTemplate: (templateId: number) => void;
  onSaveAsTemplate: () => void;
  onDispatch: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  /** 3rd-party couriers (feature-flagged: customerConfig.parts_delivery.third_party). */
  thirdPartyEnabled?: boolean;
  mode?: FulfillmentMode;
  onModeChange?: (mode: FulfillmentMode) => void;
  /** The quote/book section rendered in 3rd-party mode (ThirdPartyRunPanel). */
  thirdPartyContent?: React.ReactNode;
  /** Whether the committing write actions (Save as template, Dispatch) render; the host gates
   *  this on the product capability. Default true. */
  writeAllowed?: boolean;
  /** Whether the per-destination quick-add (a new order) renders; the host gates this on the
   *  product's create capability. Default true. */
  createAllowed?: boolean;
}

const DeliveryRunPanel: React.FC<DeliveryRunPanelProps> = ({
  run,
  drivers,
  vehicles,
  workflowsets,
  templates,
  submitting,
  onUpdateRun,
  onRemoveOrder,
  onRemoveDestination,
  onReorder,
  onQuickAdd,
  onLoadTemplate,
  onSaveAsTemplate,
  onDispatch,
  onDragOver,
  onDrop,
  thirdPartyEnabled = false,
  mode = 'own',
  onModeChange,
  thirdPartyContent,
  writeAllowed = true,
  createAllowed = true,
}) => {
  const stops: any[] = run?.stops || [];
  const orderCount = stops.reduce((n, s) => n + (s.delivery_orders?.length || 0), 0);
  const hasRun = !!run;
  const isThirdParty = thirdPartyEnabled && mode === 'third_party';

  // A PO can span multiple line-item rows sharing an order_number — group them so
  // the manifest shows one PO with its line items (and removes the PO as a unit).
  const poGroups = (orders: any[]): { key: string; poNumber: string; orders: any[] }[] => {
    const map = new Map<string, { key: string; poNumber: string; orders: any[] }>();
    for (const o of orders) {
      const key = o.order_number ? `po:${o.order_number}` : `id:${o.id}`;
      if (!map.has(key)) map.set(key, { key, poNumber: o.order_number ?? `#${o.id}`, orders: [] });
      map.get(key)!.orders.push(o);
    }
    return Array.from(map.values());
  };

  // Driver & Vehicle are collapsed to compact summary rows so the manifest
  // stays visible. Accordion — only one picker open at a time.
  const [openSetup, setOpenSetup] = React.useState<'driver' | 'vehicle' | null>(null);
  const toggleSetup = (which: 'driver' | 'vehicle') =>
    setOpenSetup(prev => (prev === which ? null : which));

  const selectedDriver = drivers.find((d: any) => d.id === run?.driver_id) || null;
  const selectedVehicle = vehicles.find((v: any) => v.id === run?.vehicle_id) || null;
  const onlineCount = drivers.filter((d: any) => d.status === 'online').length;
  const initials = (name?: string) =>
    (name || '')
      .split(' ')
      .map(p => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  // Compact, clickable summary row that expands its picker below.
  const SetupRow = ({
    open,
    onToggle,
    avatar,
    title,
    subtitle,
    titleColor,
    error,
    testId,
  }: {
    open: boolean;
    onToggle: () => void;
    avatar: React.ReactNode;
    title: string;
    subtitle?: React.ReactNode;
    titleColor?: string;
    error?: boolean;
    testId?: string;
  }) => (
    <Box
      onClick={onToggle}
      data-testid={testId}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
        border: 1, borderRadius: 1.5, cursor: 'pointer', transition: 'border-color 0.15s',
        borderColor: error ? 'error.main' : open ? 'primary.main' : 'divider',
        '&:hover': { borderColor: error ? 'error.main' : 'primary.light' },
      }}
    >
      {avatar}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 600, color: titleColor }} noWrap>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <MdKeyboardArrowDown
        size={20}
        style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
      />
    </Box>
  );

  const SectionLabel = ({ children, meta }: { children: React.ReactNode; meta?: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
      <Typography variant='overline' sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6 }}>
        {children}
      </Typography>
      {meta ? (
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
          {meta}
        </Typography>
      ) : null}
    </Box>
  );

  // Destination drag-to-reorder. dragIndex = the destination being dragged;
  // external pending→run drags leave dragIndex null and bubble to the drop zone.
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [overIndex, setOverIndex] = React.useState<number | null>(null);

  const handleCardDragStart = (e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleCardDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIndex !== null) {
      e.stopPropagation();
      setOverIndex(idx);
    }
  };
  const handleCardDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIndex !== null) {
      e.stopPropagation();
      if (dragIndex !== idx) onReorder(dragIndex, idx);
    }
    setDragIndex(null);
    setOverIndex(null);
  };
  const handleCardDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2, bgcolor: 'grey.900', color: 'common.white',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Delivery Run
          </Typography>
          <Typography variant='caption' sx={{ opacity: 0.7 }}>
            {hasRun
              ? `${stops.length} stop${stops.length !== 1 ? 's' : ''} · ${orderCount} order${orderCount !== 1 ? 's' : ''}`
              : 'No orders yet'}
          </Typography>
        </Box>
        <Chip
          label={hasRun ? 'Draft' : 'Empty'}
          size='small'
          sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'common.white', fontWeight: 600 }}
        />
      </Box>

      {/* Build + manifest — single scroll region */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Fulfillment mode: your labor vs a gig courier (flag-gated) */}
        {thirdPartyEnabled && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }} data-testid='pd-fulfillment-toggle'>
              {(
                [
                  { value: 'own', icon: '🚚', title: 'Own Driver', sub: 'your labor' },
                  { value: 'third_party', icon: '⚡', title: 'HopDrive Managed', sub: '3rd Party' },
                ] as const
              ).map(opt => {
                const active = (mode ?? 'own') === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => onModeChange?.(opt.value)}
                    data-testid={`pd-mode-${opt.value}`}
                    sx={{
                      flex: 1, px: 1.5, py: 1, border: 2, borderRadius: 1.5, cursor: 'pointer',
                      borderColor: active ? 'primary.main' : 'divider',
                      bgcolor: active ? 'action.hover' : 'transparent',
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: active ? 'primary.main' : 'primary.light' },
                    }}
                  >
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      {opt.icon} {opt.title}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      · {opt.sub}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* 3rd-party body: quote + book (own-driver selectors stay untouched below) */}
        {isThirdParty ? thirdPartyContent : null}

        {/* Selectors (stacked build flow) */}
        {!isThirdParty && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {templates.length > 0 && (
            <Box>
              <Typography
                variant='overline'
                sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, display: 'block', mb: 0.5 }}
              >
                Load Route Template
              </Typography>
              <TextField
                fullWidth select size='small'
                value={run?.template_id ?? ''}
                onChange={e => e.target.value && onLoadTemplate(Number(e.target.value))}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <MdRoute size={18} />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                <MenuItem value=''>
                  <Typography variant='body2' color='text.secondary'>
                    Start from a saved route…
                  </Typography>
                </MenuItem>
                {templates.map((t: any) => {
                  const count = Array.isArray(t.stops) ? t.stops.length : 0;
                  return (
                    <MenuItem key={t.id} value={t.id} sx={{ display: 'block' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                        {count} stop{count !== 1 ? 's' : ''}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          )}

          {hasRun ? (
            <>
              {/* Assign Driver — collapsed summary, expands to the picker */}
              <Box>
                <SectionLabel meta={`${onlineCount} online · ${drivers.length} total`}>
                  Assign Driver
                </SectionLabel>
                <SetupRow
                  open={openSetup === 'driver'}
                  onToggle={() => toggleSetup('driver')}
                  testId='pd-driver-setup-row'
                  error={!run?.driver_id}
                  avatar={
                    <Avatar
                      sx={{
                        width: 32, height: 32, fontSize: 12, fontWeight: 700,
                        bgcolor: selectedDriver ? 'secondaryAccent.main' : 'grey.200',
                        color: selectedDriver ? 'common.white' : 'text.secondary',
                      }}
                    >
                      {selectedDriver ? initials(selectedDriver.user?.display_name) : <MdPerson size={18} />}
                    </Avatar>
                  }
                  title={selectedDriver?.user?.display_name || 'Select a driver'}
                  titleColor={selectedDriver ? undefined : 'error.main'}
                  subtitle={
                    selectedDriver
                      ? selectedDriver.status === 'online' ? 'Online' : 'Offline'
                      : 'Required to dispatch this run'
                  }
                />
                {openSetup === 'driver' && (
                  <Box sx={{ mt: 1 }}>
                    <DriverSelect
                      drivers={drivers}
                      selectedDriverId={run?.driver_id ?? null}
                      onSelect={(id) => {
                        onUpdateRun('driver_id', id);
                        if (id) setOpenSetup(null);
                      }}
                      showSummary={false}
                      filterPlaceholder='Search drivers…'
                      maxHeight={220}
                      emptyMessage='No drivers for this customer'
                    />
                  </Box>
                )}
              </Box>

              {/* Vehicle — collapsed summary, expands to the picker */}
              <Box>
                <SectionLabel>Vehicle</SectionLabel>
                <SetupRow
                  open={openSetup === 'vehicle'}
                  onToggle={() => toggleSetup('vehicle')}
                  avatar={
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'text.secondary' }}>
                      {selectedVehicle ? <MdDirectionsCar size={18} /> : <MdPerson size={18} />}
                    </Avatar>
                  }
                  title={
                    selectedVehicle
                      ? `${selectedVehicle.year ?? ''} ${selectedVehicle.make ?? ''} ${selectedVehicle.model ?? ''}`.trim() ||
                        `Vehicle ${selectedVehicle.id}`
                      : 'Driver selects at start'
                  }
                  subtitle={
                    selectedVehicle
                      ? selectedVehicle.license_plate || undefined
                      : 'Driver picks their vehicle when they claim the run'
                  }
                />
                {openSetup === 'vehicle' && (
                  <Box sx={{ mt: 1 }}>
                    <PartsVehicleSelect
                      vehicles={vehicles}
                      selectedVehicleId={run?.vehicle_id ?? null}
                      onSelect={(id) => {
                        onUpdateRun('vehicle_id', id);
                        setOpenSetup(null);
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* POD Workflow */}
              {workflowsets.length > 0 && (
                <Box>
                  <Typography
                    variant='overline'
                    sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, display: 'block', mb: 0.5 }}
                  >
                    POD Workflow
                  </Typography>
                  <TextField
                    fullWidth select size='small'
                    value={run?.profile_id || ''}
                    onChange={e => onUpdateRun('profile_id', e.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BiFile size={18} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  >
                    <MenuItem value=''>
                      <Typography variant='body2' color='text.secondary'>
                        No capture required
                      </Typography>
                    </MenuItem>
                    {workflowsets.map((ws: any) => (
                      <MenuItem key={ws.id} value={ws.id} sx={{ display: 'block' }}>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {ws.name}
                        </Typography>
                        {ws.description ? (
                          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                            {ws.description}
                          </Typography>
                        ) : null}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {/* Departure Time */}
              <Box>
                <Typography
                  variant='overline'
                  sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, display: 'block', mb: 0.5 }}
                >
                  Departure Time
                </Typography>
                <TextField
                  fullWidth type='datetime-local' size='small'
                  value={run?.departure_time ? dayjs(run.departure_time).format('YYYY-MM-DDTHH:mm') : ''}
                  onChange={e => onUpdateRun('departure_time', e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <BiTime size={18} />
                        </InputAdornment>
                      ),
                    },
                    htmlInput: { step: 300 },
                  }}
                  helperText='When the driver will leave the dealership.'
                />
              </Box>
            </>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Add orders (drag, select, or load a template) to assign a driver, vehicle, and workflow.
            </Typography>
          )}
        </Box>
        )}

        {/* Manifest (drop zone) */}
        <Box onDragOver={onDragOver} onDrop={onDrop} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6 }}>
            Delivery Manifest
          </Typography>
          {hasRun && !isThirdParty && writeAllowed && (
            <Button size='small' onClick={onSaveAsTemplate} sx={{ textTransform: 'none' }}>
              Save as template
            </Button>
          )}
        </Box>

        {!hasRun || stops.length === 0 ? (
          <Box
            sx={{
              minHeight: 160, border: '2px dashed', borderColor: 'divider', borderRadius: 1.5,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', p: 3, color: 'text.secondary',
            }}
          >
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              No destinations yet
            </Typography>
            <Typography variant='caption'>
              Load a route template, select orders and click "Create Run", or drag orders here.
            </Typography>
          </Box>
        ) : (
          stops.map((dest: any, idx: number) => {
            const orders = dest.delivery_orders || [];
            return (
              <Box
                key={dest.key || dest.id}
                draggable
                onDragStart={e => handleCardDragStart(e, idx)}
                onDragOver={e => handleCardDragOver(e, idx)}
                onDrop={e => handleCardDrop(e, idx)}
                onDragEnd={handleCardDragEnd}
                sx={{
                  mb: 1.5, p: 1.5, border: '1px solid', borderRadius: 1.5,
                  borderColor: overIndex === idx && dragIndex !== null && dragIndex !== idx ? 'primary.main' : 'divider',
                  bgcolor: overIndex === idx && dragIndex !== null && dragIndex !== idx ? 'action.hover' : 'transparent',
                  opacity: dragIndex === idx ? 0.4 : 1,
                }}
              >
                {/* Destination header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab', mt: 0.25 }}>
                    <MdDragIndicator size={18} />
                  </Box>
                  <Box
                    sx={{
                      minWidth: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.800', color: 'common.white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, mt: 0.25,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
                      {dest.recipient_business || 'Destination'}
                    </Typography>
                    {dest.address ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <MdLocationOn size={12} />
                        <Typography variant='caption' noWrap title={dest.address}>
                          {dest.address}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                  {createAllowed && (
                    <Tooltip title='Add an order here'>
                      <IconButton size='small' onClick={() => onQuickAdd(dest)} disabled={submitting}>
                        <MdAdd size={18} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Remove destination'>
                    <IconButton size='small' onClick={() => onRemoveDestination(dest.key)} disabled={submitting} sx={{ color: 'error.main' }}>
                      <MdDelete size={16} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Orders at this destination */}
                <Box sx={{ pl: 4.5, mt: orders.length ? 1 : 0 }}>
                  {orders.length === 0 ? (
                    <Typography variant='caption' sx={{ color: dest.checkIn || dest.fromTemplate ? 'info.main' : 'text.secondary', fontWeight: dest.checkIn || dest.fromTemplate ? 600 : 400 }}>
                      {dest.checkIn || dest.fromTemplate
                        ? '↩ Check-in stop — no delivery; driver checks for a pickup'
                        : 'No orders yet — drag one here or use “＋”.'}
                    </Typography>
                  ) : (
                    poGroups(orders).map((g) => {
                      const multi = g.orders.length > 1;
                      const anyRush = g.orders.some((o: any) => o.priority === 'rush');
                      const qty = g.orders.reduce((n: number, o: any) => n + (o.quantity || 1), 0);
                      return (
                        <Box key={g.key} sx={{ py: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' sx={{ color: 'primary.main', fontWeight: 600, minWidth: 0 }} noWrap>
                              {g.poNumber}{multi ? ` · ${g.orders.length} items` : ''}
                            </Typography>
                            {anyRush && <Chip label='Urgent' size='small' color='error' sx={{ height: 16, fontSize: 9 }} />}
                            <Box sx={{ flex: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                              <BiPackage size={13} />
                              <Typography variant='caption' sx={{ fontWeight: 600 }}>{qty}</Typography>
                            </Box>
                            <Tooltip title={multi ? 'Remove PO from run' : 'Remove from run'}>
                              <IconButton size='small' onClick={() => g.orders.forEach((o: any) => onRemoveOrder(o.id))} disabled={submitting}>
                                <MdClose size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {multi ? (
                            <Box sx={{ pl: 0.5 }}>
                              {g.orders.map((o: any) => (
                                <Typography key={o.id} variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                                  • {o.item_description || o.part_number || 'Line item'}{o.part_number ? ` · ${o.part_number}` : ''}{(o.quantity || 1) > 1 ? ` ×${o.quantity}` : ''}
                                </Typography>
                              ))}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            );
          })
        )}
        </Box>
      </Box>

      {/* Dispatch (own-driver only — the 3rd-party action lives in its body) */}
      {!isThirdParty && writeAllowed && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth variant='contained' size='large' onClick={onDispatch}
            disabled={!hasRun || orderCount === 0 || !run?.driver_id || submitting}
            startIcon={submitting ? <CircularProgress size={18} color='inherit' /> : <MdSend />}
            data-testid='pd-dispatch'
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {submitting ? 'Dispatching…' : 'Dispatch to Driver'}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default DeliveryRunPanel;
